/**
 * Letters that are drawn identically, or near enough, in Latin, Cyrillic and Greek.
 *
 * A short, high-confidence subset of the pairs in Unicode's confusables data (UTS #39):
 * only letters that are indistinguishable in ordinary text fonts, so that a word
 * flagged as mixing them is genuinely deceptive rather than merely unusual. Used by the
 * hidden character detector to find "pаypal" with a Cyrillic а.
 */

export type Script = "latin" | "cyrillic" | "greek";

export interface Lookalike {
  script: Script;
  /** The letter's Unicode name. */
  name: string;
  /** The letter it imitates in each other script, where one exists. */
  targets: Partial<Record<Script, string>>;
}

/** [letter, Unicode name, the Latin letter it imitates]. */
const CYRILLIC: readonly (readonly [string, string, string])[] = [
  ["а", "CYRILLIC SMALL LETTER A", "a"],
  ["е", "CYRILLIC SMALL LETTER IE", "e"],
  ["о", "CYRILLIC SMALL LETTER O", "o"],
  ["р", "CYRILLIC SMALL LETTER ER", "p"],
  ["с", "CYRILLIC SMALL LETTER ES", "c"],
  ["у", "CYRILLIC SMALL LETTER U", "y"],
  ["х", "CYRILLIC SMALL LETTER HA", "x"],
  ["ѕ", "CYRILLIC SMALL LETTER DZE", "s"],
  ["і", "CYRILLIC SMALL LETTER BYELORUSSIAN-UKRAINIAN I", "i"],
  ["ј", "CYRILLIC SMALL LETTER JE", "j"],
  ["һ", "CYRILLIC SMALL LETTER SHHA", "h"],
  ["ԁ", "CYRILLIC SMALL LETTER KOMI DE", "d"],
  ["ԛ", "CYRILLIC SMALL LETTER QA", "q"],
  ["ԝ", "CYRILLIC SMALL LETTER WE", "w"],
  ["ӏ", "CYRILLIC SMALL LETTER PALOCHKA", "l"],
  ["А", "CYRILLIC CAPITAL LETTER A", "A"],
  ["В", "CYRILLIC CAPITAL LETTER VE", "B"],
  ["Е", "CYRILLIC CAPITAL LETTER IE", "E"],
  ["К", "CYRILLIC CAPITAL LETTER KA", "K"],
  ["М", "CYRILLIC CAPITAL LETTER EM", "M"],
  ["Н", "CYRILLIC CAPITAL LETTER EN", "H"],
  ["О", "CYRILLIC CAPITAL LETTER O", "O"],
  ["Р", "CYRILLIC CAPITAL LETTER ER", "P"],
  ["С", "CYRILLIC CAPITAL LETTER ES", "C"],
  ["Т", "CYRILLIC CAPITAL LETTER TE", "T"],
  ["Х", "CYRILLIC CAPITAL LETTER HA", "X"],
  ["Ѕ", "CYRILLIC CAPITAL LETTER DZE", "S"],
  ["І", "CYRILLIC CAPITAL LETTER BYELORUSSIAN-UKRAINIAN I", "I"],
  ["Ј", "CYRILLIC CAPITAL LETTER JE", "J"],
  ["Ү", "CYRILLIC CAPITAL LETTER STRAIGHT U", "Y"],
  ["Ԛ", "CYRILLIC CAPITAL LETTER QA", "Q"],
  ["Ԝ", "CYRILLIC CAPITAL LETTER WE", "W"],
  ["Ӏ", "CYRILLIC LETTER PALOCHKA", "I"],
];

const GREEK: readonly (readonly [string, string, string])[] = [
  ["ο", "GREEK SMALL LETTER OMICRON", "o"],
  ["ν", "GREEK SMALL LETTER NU", "v"],
  ["ρ", "GREEK SMALL LETTER RHO", "p"],
  ["α", "GREEK SMALL LETTER ALPHA", "a"],
  ["ι", "GREEK SMALL LETTER IOTA", "i"],
  ["υ", "GREEK SMALL LETTER UPSILON", "u"],
  ["Α", "GREEK CAPITAL LETTER ALPHA", "A"],
  ["Β", "GREEK CAPITAL LETTER BETA", "B"],
  ["Ε", "GREEK CAPITAL LETTER EPSILON", "E"],
  ["Ζ", "GREEK CAPITAL LETTER ZETA", "Z"],
  ["Η", "GREEK CAPITAL LETTER ETA", "H"],
  ["Ι", "GREEK CAPITAL LETTER IOTA", "I"],
  ["Κ", "GREEK CAPITAL LETTER KAPPA", "K"],
  ["Μ", "GREEK CAPITAL LETTER MU", "M"],
  ["Ν", "GREEK CAPITAL LETTER NU", "N"],
  ["Ο", "GREEK CAPITAL LETTER OMICRON", "O"],
  ["Ρ", "GREEK CAPITAL LETTER RHO", "P"],
  ["Τ", "GREEK CAPITAL LETTER TAU", "T"],
  ["Υ", "GREEK CAPITAL LETTER UPSILON", "Y"],
  ["Χ", "GREEK CAPITAL LETTER CHI", "X"],
];

const UPPERCASE = /^\p{Lu}$/u;

function latinName(letter: string): string {
  const size = UPPERCASE.test(letter) ? "CAPITAL" : "SMALL";
  return `LATIN ${size} LETTER ${letter.toUpperCase()}`;
}

function buildLookalikes(): Map<string, Lookalike> {
  const map = new Map<string, Lookalike>();
  const addForeign = (
    script: Script,
    rows: readonly (readonly [string, string, string])[]
  ) => {
    for (const [letter, name, latin] of rows) {
      map.set(letter, { script, name, targets: { latin } });
      // The reverse direction: the Latin letter is the impostor in a word of this
      // script. The first foreign letter listed for a Latin letter wins.
      const entry = map.get(latin) ?? {
        script: "latin" as const,
        name: latinName(latin),
        targets: {},
      };
      entry.targets[script] ??= letter;
      map.set(latin, entry);
    }
  };
  addForeign("cyrillic", CYRILLIC);
  addForeign("greek", GREEK);
  return map;
}

/** Every letter with a look-alike in another script, keyed by the letter. */
export const LOOKALIKE_LETTERS: ReadonlyMap<string, Lookalike> =
  buildLookalikes();
